/**
 * Custom hook for fetching sports markets
 * Uses React Query with proper caching and data transformation
 */

import { useQuery } from '@tanstack/react-query';
import {
  fetchAllInPlay,
  fetchAllMatches,
  fetchInPlayBySportId,
  fetchMatchesBySportId,
  fetchMatchesToday,
  fetchMatchesTomorrow,
  fetchMatchesTodayBySportId,
  fetchMatchesTomorrowBySportId,
  fetchMarketOdds,
  collectMarkets,
  transformMarketToMatch,
  SPORT_NAME_MAP,
} from '@/services/sports/marketApiService';

/**
 * Fetch markets based on tab and sport ID
 * @param {string|null} sportId - Sport ID or null for all sports
 * @param {string} upperTab - Tab type: 'in-play', 'today', 'tomorrow'
 * @returns {Object} React Query result with matches data
 */
export const useSportsMarkets = (sportId, upperTab = 'in-play') => {
  return useQuery({
    queryKey: ['sportsMarkets', sportId, upperTab],
    queryFn: async () => {
      let rawMarkets = [];

      // Fetch markets based on tab and sport
      if (upperTab === 'in-play') {
        if (!sportId) {
          // All sports - fetch all in-play
          const [inPlayData, allMatchesData] = await Promise.all([
            fetchAllInPlay(),
            fetchAllMatches(),
          ]);

          const inPlayMarkets = collectMarkets(inPlayData).map((m) => ({
            ...m,
            inplay: true,
          }));
          const allMarkets = collectMarkets(allMatchesData);

          // Merge with in-play preferred
          const map = new Map();
          inPlayMarkets.forEach((m) => m?.marketId && map.set(m.marketId, m));
          allMarkets.forEach((m) => {
            if (m?.marketId && !map.has(m.marketId)) map.set(m.marketId, m);
          });
          rawMarkets = Array.from(map.values());
        } else {
          // Specific sport - in-play
          const [inPlayData, matchesData] = await Promise.all([
            fetchInPlayBySportId(sportId),
            fetchMatchesBySportId(sportId),
          ]);

          const games1 = inPlayData?.games || inPlayData || [];
          const games2 = matchesData?.games || matchesData || [];
          const sportName = SPORT_NAME_MAP[sportId] || 'Unknown';

          const markets1 = Array.isArray(games1)
            ? games1.flatMap((g) =>
                (g?.markets || []).map((m) => ({ ...m, sportId, sportName }))
              )
            : [];
          const markets2 = Array.isArray(games2)
            ? games2.flatMap((g) =>
                (g?.markets || []).map((m) => ({ ...m, sportId, sportName }))
              )
            : [];

          rawMarkets = [
            ...markets1.map((m) => ({ ...m, inplay: true })),
            ...markets2.map((m) => ({ ...m, inplay: false })),
          ];
        }
      } else if (upperTab === 'today') {
        if (!sportId) {
          // All sports - today
          const data = await fetchMatchesToday();
          rawMarkets = collectMarkets(data);
        } else {
          // Specific sport - today
          const data = await fetchMatchesTodayBySportId(sportId);
          if (Array.isArray(data)) {
            if (data.length > 0 && data[0]?.markets) {
              rawMarkets = collectMarkets(data);
            } else {
              rawMarkets = data;
            }
          } else {
            rawMarkets = collectMarkets(data);
          }
        }
      } else if (upperTab === 'tomorrow') {
        if (!sportId) {
          // All sports - tomorrow
          const data = await fetchMatchesTomorrow();
          rawMarkets = collectMarkets(data);
        } else {
          // Specific sport - tomorrow
          const data = await fetchMatchesTomorrowBySportId(sportId);
          if (Array.isArray(data)) {
            if (data.length > 0 && data[0]?.markets) {
              rawMarkets = collectMarkets(data);
            } else {
              rawMarkets = data;
            }
          } else {
            rawMarkets = collectMarkets(data);
          }
        }
      }

      // Transform markets to match format and fetch odds
      // Use Promise.allSettled to prevent one failed odds fetch from blocking all matches
      const matchPromises = rawMarkets.map(async (m) => {
        const sportName = m.sportName || SPORT_NAME_MAP[m.sportId] || 'Unknown';
        const match = transformMarketToMatch(m, upperTab, sportName);

        // Fetch odds if:
        // 1. It's in-play (always fetch for in-play)
        // 2. Or if the market data doesn't have odds (needsOddsFetch flag)
        if (upperTab === 'in-play' || match.needsOddsFetch) {
          try {
            const odds = await fetchMarketOdds(match.id);
            if (odds) {
              match.odds = odds;
              match.totalMatched = odds.totalMatched || 0;
            }
          } catch (err) {
            // If odds fetch fails, continue with default odds (don't block)
            console.warn(`Failed to fetch odds for match ${match.id}:`, err);
          }
        } else {
          // For today/tomorrow with odds in data, calculate PTE from runners
          try {
            match.totalMatched = (m.runners || []).reduce(
              (sum, r) => sum + (r.totalMatched || 0),
              0
            ) || match.odds.totalMatched || 0;
          } catch {
            match.totalMatched = match.odds.totalMatched || 0;
          }
        }

        // Remove the flag before returning
        delete match.needsOddsFetch;
        return match;
      });

      // Use allSettled to ensure all matches are processed even if some odds fetches fail
      const results = await Promise.allSettled(matchPromises);
      const matches = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);

      // Sort: in-play first, then by start time
      matches.sort((a, b) => {
        if (a.isLive && !b.isLive) return -1;
        if (!a.isLive && b.isLive) return 1;
        return new Date(a.date) - new Date(b.date);
      });

      return matches;
    },
    enabled: true,
    staleTime: upperTab === 'in-play' ? 5 * 1000 : 30 * 1000, // 5s for in-play, 30s for today/tomorrow
    refetchInterval: upperTab === 'in-play' ? 10 * 1000 : 60 * 1000, // 10s for in-play, 60s for today/tomorrow
    refetchIntervalInBackground: upperTab === 'in-play', // Only background refetch for in-play
    retry: 2,
    retryDelay: 1000,
  });
};
